import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ReactFlowProvider } from "@xyflow/react";
import { useFlowData } from "../api/useFlowData";
import { useFlowLayout } from "../hooks/useFlowLayout";
import { useFlowInteractions } from "../hooks/useFlowInteractions";
import { FlowCanvas } from "./FlowCanvas";
import { FlowControls } from "./controls/FlowControls";
import { LoadingOverlay } from "./controls/LoadingOverlay";
import "../styles/flow.css";
/**
 * Internal component that uses React Flow hooks
 * Must be wrapped in ReactFlowProvider
 */
function WorkspaceFlowVizInner({ workspaceId, height = "100vh", width = "100%", onNodeClick, className = "", }) {
    // Fetch and aggregate data from multiple sources
    const { nodes, edges, isLoading, errors } = useFlowData(workspaceId);
    // Calculate automatic layout for nodes with much larger spacing (30% increase)
    const layoutedNodes = useFlowLayout(nodes, edges, {
        direction: "TB",
        nodeSpacing: 156,
        rankSpacing: 234,
    });
    // Handle interactions (selection, highlighting)
    const { selectedNodeId, highlightedEdges, handleNodeClick, clearSelection } = useFlowInteractions(edges);
    // Retry function for error recovery
    const handleRetry = () => {
        window.location.reload();
    };
    // Handle node click and call optional callback
    const handleNodeClickWithCallback = (event, node) => {
        handleNodeClick(event, node);
        if (onNodeClick && node.data) {
            onNodeClick(node.data);
        }
    };
    // Apply selection and highlighting to nodes and edges
    const nodesWithSelection = layoutedNodes.map((node) => ({
        ...node,
        selected: node.id === selectedNodeId,
        className: node.id === selectedNodeId ? "selected" : "",
    }));
    const edgesWithHighlight = edges.map((edge) => ({
        ...edge,
        type: "straight",
        animated: highlightedEdges.includes(edge.id),
        className: highlightedEdges.includes(edge.id) ? "highlighted" : "",
    }));
    // Loading state
    if (isLoading) {
        return (_jsx("div", { className: `workspace-flow-viz ${className}`, style: { height, width, position: "relative" }, children: _jsx(LoadingOverlay, {}) }));
    }
    // Error state - show error if we have errors and no data
    const hasErrors = Object.values(errors).some((error) => error !== undefined);
    if (hasErrors && layoutedNodes.length === 0) {
        return (_jsx("div", { className: `workspace-flow-viz ${className}`, style: { height, width }, children: _jsx("div", { className: "flow-error-state", children: _jsxs("div", { className: "flow-error-content", children: [_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 48 48", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "flow-error-icon", children: [_jsx("circle", { cx: "24", cy: "24", r: "22", stroke: "var(--flow-node-text, #000)", strokeWidth: "2" }), _jsx("path", { d: "M24 14V26M24 32V34", stroke: "var(--flow-node-text, #000)", strokeWidth: "2", strokeLinecap: "round" })] }), _jsx("h3", { className: "flow-error-title", children: "Failed to load visualization" }), _jsx("p", { className: "flow-error-message", children: errors.workspace
                                ? "Unable to load workspace data"
                                : errors.projects
                                    ? "Unable to load project data"
                                    : "An error occurred while loading data" }), _jsx("button", { onClick: handleRetry, className: "flow-error-retry-button", children: "Retry" })] }) }) }));
    }
    // Empty state - no data to visualize
    if (layoutedNodes.length === 0) {
        return (_jsx("div", { className: `workspace-flow-viz ${className}`, style: { height, width }, children: _jsx("div", { className: "flow-empty-state", children: _jsxs("div", { className: "flow-empty-content", children: [_jsxs("svg", { width: "64", height: "64", viewBox: "0 0 64 64", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "flow-empty-icon", children: [_jsx("rect", { x: "8", y: "8", width: "48", height: "48", rx: "4", stroke: "var(--flow-accent, #bdbdbd)", strokeWidth: "2", strokeDasharray: "4 4" }), _jsx("circle", { cx: "32", cy: "32", r: "8", stroke: "var(--flow-accent, #bdbdbd)", strokeWidth: "2" })] }), _jsx("h3", { className: "flow-empty-title", children: "No data to visualize" }), _jsx("p", { className: "flow-empty-message", children: "Create a workspace and add projects to see the visualization" })] }) }) }));
    }
    // Render the flow canvas with data
    return (_jsxs("div", { className: `workspace-flow-viz ${className}`, style: { height, width, position: "relative" }, children: [_jsx(FlowCanvas, { nodes: nodesWithSelection, edges: edgesWithHighlight, onNodeClick: handleNodeClickWithCallback, showMiniMap: false }), _jsx(FlowControls, {})] }));
}
/**
 * WorkspaceFlowViz - Main component for workspace flow visualization
 *
 * Provides an interactive, graph-based view of the relationships between
 * workspaces, projects, tasks, and notifications using React Flow.
 *
 * Features:
 * - Automatic hierarchical layout
 * - Pan and zoom interactions
 * - Node selection and edge highlighting
 * - Loading, error, and empty states
 * - Responsive design
 *
 * @example
 * ```tsx
 * <WorkspaceFlowViz
 *   workspaceId="workspace-123"
 *   height="calc(100vh - 64px)"
 *   onNodeClick={(node) => console.log('Clicked:', node)}
 * />
 * ```
 */
export function WorkspaceFlowViz(props) {
    return (_jsx(ReactFlowProvider, { children: _jsx(WorkspaceFlowVizInner, { ...props }) }));
}
